import { useState, useEffect } from 'react';
import RequestBuilder from 'Resources/RequestBuilder';
import ComparisonTypes from 'Resources/ComparisonTypes';
import ClientResource from 'App/resources/Client';
import { makeCancelable } from '@upsales/components/Utils/CancelablePromise';
import logError from 'Helpers/logError';

type UseProspectingCountResult = {
	prospectingCount: number;
	isLoading: boolean;
};

type UseProspectingCountParams = {
	filters: RequestBuilder;
	conditionsNotMet: boolean;
};

export const useProspectingCount = ({
	filters,
	conditionsNotMet
}: UseProspectingCountParams): UseProspectingCountResult => {
	const [prospectingCount, setProspectingCount] = useState<number>(0);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	useEffect(() => {
		// Don't make request if conditions aren't met
		if (conditionsNotMet) {
			setProspectingCount(0);
			setIsLoading(false);
			return;
		}

		let isCancelled = false;

		const fetchProspectingCount = async () => {
			setIsLoading(true);

			try {
				// Create a copy of the filters to avoid mutating the original
				const prospectingFilters = filters.clone();

				// Add filter for prospectingId IS NOT NULL to get only prospecting companies
				prospectingFilters.addFilter({ field: 'prospectingId' }, ComparisonTypes.NotEquals, null);

				// Set limit to 0 to get count without data
				prospectingFilters.limit = 0;

				const { promise } = makeCancelable(ClientResource.find(prospectingFilters.build()));
				const result = await promise;

				// Only update state if the effect hasn't been cancelled
				if (!isCancelled) {
					setProspectingCount(result.metadata?.total ?? 0);
				}
			} catch (error) {
				// Only update state if the effect hasn't been cancelled
				if (!isCancelled) {
					logError(error, 'Failed to fetch prospecting count');
					setProspectingCount(0);
				}
			} finally {
				// Only update loading state if the effect hasn't been cancelled
				if (!isCancelled) {
					setIsLoading(false);
				}
			}
		};

		fetchProspectingCount();

		// Return cleanup function for useEffect
		return () => {
			isCancelled = true;
		};
	}, [filters, conditionsNotMet]);

	return {
		prospectingCount,
		isLoading
	};
};
